import dayjs from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import { noneItem } from '@/libs/noneOption'
import { useFetchStore } from '@/stores/fetch'
import type { SelectOption } from '@/types/Common'
import type {
    IStaffVacationBalance,
    IStaffVacationBreakdown,
    IStaffVacationDecision,
    IStaffVacationDecisionBody,
    IStaffVacationHistory,
    IStaffVacationSettings,
} from '@/types/StaffVacation'

/**
 * The yearly leave entitlement, as one client (docs/OSEBJE-UI.md §5).
 *
 * Split the way `libs/staffAbsence.ts` is split, and for the same reason: the personal
 * reads are addresses OUTSIDE `/properties` and the house's are inside it, and the
 * difference IS the permission model. One file, so it stays visible which is which.
 *
 * Every call answers a value rather than a response, and a refusal answers the empty
 * one. A screen that had to unwrap a status code per read would eventually unwrap one
 * of them wrongly and draw a blank card where a refusal happened.
 */

/**
 * Where the API answers from, written out because a LINK cannot go through the fetch
 * store. The same literal `stores/fetch.ts` passes to `createFetch`, and a literal in
 * both places rather than one exported from the other — `libs/staffAbsence.ts` says why.
 */
const API_BASE = '/api/pms/v1'

/** The house's defaults until it has answered. They are the statutory floor and nothing more. */
export const emptyVacationSettings = (): IStaffVacationSettings => ({
    base_days:           20,
    tenure_tiers:        [],
    education_tiers:     [],
    responsibility_days: 0,
    special_days:        0,
    child_days:          0,
    child_age_limit:     15,
    proration:           'full_months',
    carry_over_until:    { month: 6, day: 30 },
})

export const emptyVacationBreakdown = (): IStaffVacationBreakdown => ({
    base:                 0,
    tenure:               0,
    education:            0,
    responsibility:       0,
    special:              0,
    children:             0,
    service_years:        0,
    children_under_limit: 0,
    full_year_total:      0,
    months_employed:      12,
    prorated:             false,
    override:             0,
})

// MARK: - The rules. `staff:manage`, like every other register in this module.

export const loadVacationSettings = async (propertyId: string): Promise<IStaffVacationSettings | null> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/vacation/settings`)
        .get()
        .json<IStaffVacationSettings>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

export const saveVacationSettings = async (
    propertyId: string,
    settings: IStaffVacationSettings,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/vacation/settings`)
        .put(settings)
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/**
 * The same arithmetic the service does, done again in the browser for ONE purpose: the
 * worked example under the settings form.
 *
 * It is a duplicate and it is meant to be. A house setting a ladder cannot see what the
 * ladder does — the next decision it touches may be issued in March — and a setting whose
 * consequence is invisible is a setting that gets set wrong. So the form shows „8 let
 * dobe, VII, 1 otrok → 24 dni“ and moves it as the numbers move.
 *
 * It decides NOTHING. No decision is issued from this number, no balance is drawn from
 * it, and where it and the service disagree the service is right — which is the whole
 * reason a decision carries the breakdown it was issued with rather than one recomputed
 * on the way to the screen.
 *
 * The ladders are read as "the highest rung reached", not as a sum of rungs: a person
 * with eleven years of service gets the eleven-year row, not the five-year row plus it.
 */
export const exampleVacationDays = (
    settings: IStaffVacationSettings,
    input: { tenureYears: number, educationLevel: string, children: number, responsible: boolean, special: boolean },
): { total: number, breakdown: IStaffVacationBreakdown } => {
    const breakdown = emptyVacationBreakdown()

    breakdown.base = settings.base_days

    breakdown.tenure = [...settings.tenure_tiers]
        .sort((left, right) => left.from_years - right.from_years)
        .filter(tier => input.tenureYears >= tier.from_years)
        .reduce((highest, tier) => Math.max(highest, tier.days), 0)

    // Matched by prefix, so one row for "VII" answers both VII/1 and VII/2 — the levels
    // a Slovenian personnel file actually carries.
    breakdown.education = settings.education_tiers
        .filter(tier => tier.level_prefix && input.educationLevel.startsWith(tier.level_prefix))
        .reduce((highest, tier) => Math.max(highest, tier.days), 0)

    breakdown.responsibility = input.responsible ? settings.responsibility_days : 0
    breakdown.special        = input.special ? settings.special_days : 0
    breakdown.children       = Math.max(0, input.children) * settings.child_days

    const total = breakdown.base + breakdown.tenure + breakdown.education
        + breakdown.responsibility + breakdown.special + breakdown.children

    breakdown.service_years        = Math.max(0, input.tenureYears)
    breakdown.children_under_limit = Math.max(0, input.children)
    breakdown.full_year_total      = total

    return { total, breakdown }
}

/**
 * The breakdown as rows, in the order the decision prints them. Zeroes included.
 *
 * Six components and then the whole-year sum, which is what the service stores. Proration
 * is deliberately NOT a row of days here: the service prorates the TOTAL once and keeps
 * the months rather than a number of days taken off, and a screen subtracting its own
 * figure would print a different sum from the one on the document. The two panels say
 * „sorazmerni del za N mesecev“ in words beside these rows instead.
 */
export const breakdownRows = (breakdown: IStaffVacationBreakdown): { label: string, days: number }[] => [
    { label: i18n.global.t('staff_vacation.part_base'),           days: breakdown.base },
    { label: i18n.global.t('staff_vacation.part_tenure'),         days: breakdown.tenure },
    { label: i18n.global.t('staff_vacation.part_education'),      days: breakdown.education },
    { label: i18n.global.t('staff_vacation.part_responsibility'), days: breakdown.responsibility },
    { label: i18n.global.t('staff_vacation.part_special'),        days: breakdown.special },
    { label: i18n.global.t('staff_vacation.part_children'),       days: breakdown.children },
    { label: i18n.global.t('staff_vacation.part_full_year'),      days: breakdown.full_year_total },
]

/**
 * The levels a Slovenian personnel file carries, as a picker.
 *
 * A free-text box here would defeat the education ladder entirely: "VII/2", "VII-2" and
 * "vii/2" are one level and three strings, and the tier matches by prefix.
 */
export const educationLevels = (): SelectOption[] => [
    noneItem(i18n.global.t('staff_vacation.education_unset')),
    { value: 'I',      label: 'I' },
    { value: 'II',     label: 'II' },
    { value: 'III',    label: 'III' },
    { value: 'IV',     label: 'IV' },
    { value: 'V',      label: 'V' },
    { value: 'VI/1',   label: 'VI/1' },
    { value: 'VI/2',   label: 'VI/2' },
    { value: 'VII/1',  label: 'VII/1' },
    { value: 'VII/2',  label: 'VII/2' },
    { value: 'VIII/1', label: 'VIII/1' },
    { value: 'VIII/2', label: 'VIII/2' },
    { value: 'IX',     label: 'IX' },
]

// MARK: - The balances. `staff:time` on the house's, none at all on one's own.

export const loadVacationBalances = async (
    propertyId: string,
    year: number,
    departmentId: string,
): Promise<IStaffVacationBalance[]> => {
    const scope = departmentId ? `&department_id=${departmentId}` : ''

    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/vacation?year=${year}${scope}`)
        .get()
        .json<IStaffVacationBalance[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/** My own. No property and no permission: the API narrows to the signed-in account. */
export const loadMyVacation = async (year: number): Promise<IStaffVacationBalance | null> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/staff/me/vacation?year=${year}`)
        .get()
        .json<IStaffVacationBalance>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

/**
 * My own balance WITH the stretches of leave behind it.
 *
 * One call and not two: the service reads the record once and answers both, so the list
 * always adds up to the total drawn above it. A screen that fetched the balance and the
 * history separately would eventually disagree with itself because an approval landed
 * between the two reads.
 */
export const loadMyVacationHistory = async (year: number): Promise<IStaffVacationHistory | null> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/staff/me/vacation-history?year=${year}`)
        .get()
        .json<IStaffVacationHistory>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

/** One person's, for the manager looking at their card. `staff:time`. */
export const loadVacationHistoryFor = async (
    propertyId: string,
    userId: string,
    year: number,
): Promise<IStaffVacationHistory | null> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/vacation/history?user_id=${userId}&year=${year}`)
        .get()
        .json<IStaffVacationHistory>()

    return statusCode.value === 200 ? (data.value ?? null) : null
}

// MARK: - The decisions themselves.

export const loadVacationDecisions = async (
    propertyId: string,
    employmentId: string,
): Promise<IStaffVacationDecision[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/employments/${employmentId}/vacation-decisions`)
        .get()
        .json<IStaffVacationDecision[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

/** My own decisions, every year of them. The bell reads this too. */
export const loadMyVacationDecisions = async (): Promise<IStaffVacationDecision[]> => {
    const { statusCode, data } = await useFetchStore()
        .fetch('/staff/me/vacation-decisions')
        .get()
        .json<IStaffVacationDecision[]>()

    return statusCode.value === 200 ? (data.value ?? []) : []
}

export const issueVacationDecision = async (
    propertyId: string,
    employmentId: string,
    body: IStaffVacationDecisionBody,
): Promise<{ ok: boolean, decision: IStaffVacationDecision | null, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/properties/${propertyId}/staff/employments/${employmentId}/vacation-decisions`)
        .post(body)
        .json<IStaffVacationDecision>()

    const ok = response.value?.status === 200 || response.value?.status === 201

    return { ok, decision: ok ? (data.value ?? null) : null, body: data.value }
}

/**
 * „Izdaj odloke za leto N“ — one call per person, in sequence.
 *
 * There is no bulk route in the contract and this does not invent one: it is the same
 * single-decision call the row button makes, made for everybody who has no decision yet.
 * In sequence rather than in parallel because the alternative is a hotel of forty people
 * opening forty writes at once, and because a partial result has to be reportable — the
 * answer says how many were issued and how many refused, and the screen prints both.
 *
 * It is deliberately not idempotent-by-name: anybody who already HAS a decision for the
 * year is skipped by the caller, which is the only rule that matters here. Re-issuing an
 * existing decision is a decision the person has to make one row at a time.
 */
export const issueVacationDecisions = async (
    propertyId: string,
    employmentIds: string[],
    year: number,
): Promise<{ issued: number, failed: number, lastError: unknown }> => {
    let issued    = 0
    let failed    = 0
    let lastError: unknown = null

    for (const employmentId of employmentIds) {
        const { ok, body } = await issueVacationDecision(propertyId, employmentId, { year, note: '' })

        if (ok) {
            issued += 1
        } else {
            failed   += 1
            lastError = body
        }
    }

    return { issued, failed, lastError }
}

/**
 * The decision as a document. A LINK, for the reason the monthly export is one.
 *
 * The owner or `staff:manage` may open it, which the service decides — this only builds
 * the address.
 */
export const vacationDecisionPDFURL = (propertyId: string, decisionId: string): string =>
    `${API_BASE}/properties/${propertyId}/staff/vacation-decisions/${decisionId}/pdf`

/**
 * „Potrjujem“ — the person saying they have read their own decision.
 *
 * It acknowledges RECEIPT and not agreement, which the screen says in words. §8 makes
 * that distinction about an overtime confirmation and it is the same distinction here: a
 * signature that quietly means "and I accept the number" is a trap.
 */
export const acknowledgeMyVacationDecision = async (
    decisionId: string,
): Promise<{ ok: boolean, body: unknown }> => {
    const { response, data } = await useFetchStore()
        .fetch(`/staff/me/vacation-decisions/${decisionId}/acknowledge`)
        .post({})
        .json()

    return { ok: response.value?.status === 200, body: data.value }
}

/** Go's zero time is how the API spells "not yet acknowledged". */
export const isAcknowledged = (decision: IStaffVacationDecision): boolean =>
    !!decision.acknowledged_at && !decision.acknowledged_at.startsWith('0001-01-01')

/**
 * „24 dni“.
 *
 * Interpolated rather than pluralised, which is the convention of every other counted
 * label in this portal (`staff_absence.days_recorded`, `staff.works_in`). Slovenian has
 * four plural forms and a message format that carries all four is a message somebody
 * eventually translates into two — the number does the work and the noun stays put.
 */
export const formatDays = (days?: number | null): string =>
    i18n.global.t('staff_vacation.days_count', { count: Math.trunc(days ?? 0) })

/**
 * The day the old leave lapses, as a person says it: „30. 6. 2026“.
 *
 * Empty where the API sent nothing, which the screens draw as no deadline line at all
 * rather than as a dash — a deadline nobody knows is worse than no sentence about one.
 */
export const carryOverDeadlineLabel = (balance: IStaffVacationBalance | null): string =>
    balance?.carry_over_deadline
        ? dayjs(balance.carry_over_deadline).format('D. M. YYYY')
        : ''

/**
 * How many days there are until the old leave lapses — negative once it has.
 *
 * It exists to decide the TONE of the old-leave row and nothing else. Sixty days is the
 * threshold the screens use: two months is long enough to plan a week off around and short
 * enough that being told about it changes what somebody does.
 */
export const daysUntilCarryOverDeadline = (balance: IStaffVacationBalance | null): number => {
    if (!balance?.carry_over_deadline) return Number.MAX_SAFE_INTEGER

    return dayjs(balance.carry_over_deadline).startOf('day').diff(dayjs().startOf('day'), 'day')
}
